/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  showSessionExpiredToast,
  clearSessionExpiredToast,
} from '@/lib/session-expired-toast'
import { supabase } from '@/lib/supabase'
import { getSupabaseUrl } from '@/lib/runtime-env'
import { clearTenantDbCache } from '@/lib/db-tenant'
import { clearTenantClients } from '@/lib/supabase'
import { UNIFIED_TEAM_INVITATIONS_KEYS } from '@/hooks/useOrganizationInvitations'

interface AuthState {
  user: User | null
  session: Session | null
  isLoading: boolean
  /**
   * True when a valid aal1 session exists but the user must complete MFA
   * verification before accessing protected routes. The session is kept alive
   * so the MFA challengeAndVerify call can succeed without re-entering password.
   */
  isMFAPending: boolean
}

interface SignInResult {
  data: { user: User | null; session: Session | null }
  error: AuthError | null
  /** True when the user has TOTP MFA enabled and must complete a challenge before proceeding */
  requiresMFA?: boolean
}

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<SignInResult>
  signOut: () => Promise<{ error: AuthError | null }>
  /**
   * Finalizes login after a successful MFA challenge.
   * Re-checks organization suspension, then reads the current Supabase session
   * (now aal2), clears isMFAPending, and sets user in state.
   */
  completeMFALogin: () => Promise<{ error: AuthError | null }>
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

/**
 * Auth context provider
 * Provides shared auth state across all components
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    isLoading: true,
    isMFAPending: false,
  })

  // Block auth state updates during login verification
  const isVerifyingRef = useRef(false)

  /**
   * Distinguishes an explicit user-initiated Sign Out (button click) from a
   * silent sign-out caused by token expiry. The signOut() method sets this
   * to true BEFORE calling supabase.auth.signOut(), so the SIGNED_OUT listener
   * can suppress the session-expired toast for the intentional case.
   */
  const isExplicitSignOutRef = useRef(false)

  // Keep `navigate` in a ref so the onAuthStateChange effect does not
  // re-subscribe every time react-router returns a new navigate function
  // (react-router returns a fresh identity on every render). Re-subscribing
  // would re-run getSession() and briefly resurrect a just-cleared session.
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  /**
   * Cancels in-flight queries and clears the cache in the correct order.
   * Used by both token-expiry (SIGNED_OUT) and explicit signOut() flows so
   * both paths drop stale data without leaving pending queries that could
   * retry with a dead token. `cancelQueries` is async — awaiting it ensures
   * in-flight requests finish cancelling before we wipe the cache, so a
   * late response can't briefly re-populate a just-cleared entry.
   */
  const cleanupQueryClient = useCallback(async () => {
    await queryClient.cancelQueries()
    queryClient.clear()
  }, [queryClient])

  useEffect(() => {
    if (!supabase) {
      setState({ user: null, session: null, isLoading: false, isMFAPending: false })
      return
    }

    /**
     * Helper: checks whether a session requires MFA verification.
     * Returns true when the user has enrolled TOTP factors (nextLevel=aal2)
     * but the current JWT is only aal1 (password-only).
     */
    const checkMFAPending = async (): Promise<boolean> => {
      try {
        const { data: aal } = await supabase!.auth.mfa.getAuthenticatorAssuranceLevel()
        return !!(aal && aal.nextLevel === 'aal2' && aal.currentLevel !== 'aal2')
      } catch (error) {
        // If AAL check fails, don't block access — better than a permanent spinner
        console.error('MFA assurance level check failed:', error)
        return false
      }
    }

    // Get initial session with MFA assurance level check.
    // If the session is aal1 but MFA is required, keep the Supabase session alive
    // (so challengeAndVerify works) but set user=null + isMFAPending=true so
    // ProtectedRoute redirects to /login where the MFA step is shown.
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (isVerifyingRef.current) return

      if (session) {
        const mfaPending = await checkMFAPending()
        if (mfaPending) {
          setState({ user: null, session: null, isLoading: false, isMFAPending: true })
          return
        }
      }

      setState({
        user: session?.user ?? null,
        session,
        isLoading: false,
        isMFAPending: false,
      })
    })

    // Listen for auth changes (but ignore during login verification).
    //
    // INITIAL_SESSION is skipped — the getSession() call above handles it
    // with a proper async MFA check. Without this skip, the listener would
    // set user from the aal1 session before getSession's AAL check resolves,
    // briefly granting access to protected routes.
    //
    // For subsequent events, preserve the current isMFAPending state via
    // functional setState — only signIn() and completeMFALogin() toggle it.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') return
      if (isVerifyingRef.current) return

      if (event === 'SIGNED_OUT') {
        // Determine whether this sign-out was user-initiated (Sign Out button)
        // or caused by token expiry / revoked session. Reset the flag immediately
        // so the next SIGNED_OUT event defaults to "token expired" again.
        const wasExplicit = isExplicitSignOutRef.current
        isExplicitSignOutRef.current = false

        setState({ user: null, session: null, isLoading: false, isMFAPending: false })
        clearTenantDbCache()
        clearTenantClients()

        if (!wasExplicit) {
          // Token expired (or session revoked) — persistent toast + redirect.
          // Order matters: navigate FIRST so any active hooks unmount before we
          // cancel + clear queries; otherwise they'd refetch with a dead token
          // between cancel and unmount, causing extra 401s and SIGNED_OUT churn.
          //
          // Per DCP-130 Scenario 5: with multiple tabs, Supabase broadcasts
          // SIGNED_OUT to every tab; show the toast only in the visible tab so
          // background tabs don't stack duplicate notifications. The navigate +
          // cleanup still run everywhere so all tabs end up on /login.
          if (typeof document === 'undefined' || document.visibilityState === 'visible') {
            showSessionExpiredToast()
          }
          navigateRef.current('/login', { replace: true })
          cleanupQueryClient()
        }
      } else {
        // A sign-in that did NOT come from this tab's own signIn/completeMFALogin
        // (those clear it themselves, behind the isVerifyingRef guard above):
        // another tab signed in and Supabase broadcast it here, so this tab's
        // expiry notice is stale too (DCP-365 AC.4).
        if (event === 'SIGNED_IN') {
          clearSessionExpiredToast()
        }
        // TOKEN_REFRESHED, USER_UPDATED, SIGNED_IN (from another tab):
        // update session data but preserve isMFAPending so MFA can't be bypassed
        setState((prev) => ({
          ...prev,
          user: prev.isMFAPending ? null : (session?.user ?? null),
          session: prev.isMFAPending ? null : session,
          isLoading: false,
        }))
      }

      // Invalidate caches on meaningful auth events (not TOKEN_REFRESHED)
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
        queryClient.invalidateQueries({ queryKey: ['profile'] })
        queryClient.invalidateQueries({ queryKey: ['accessible-organizations'] })
        queryClient.invalidateQueries({ queryKey: ['banking'] })
        queryClient.invalidateQueries({ queryKey: ['plaid'] })
        queryClient.invalidateQueries({ queryKey: ['facilities'] })
        queryClient.invalidateQueries({ queryKey: ['team-members'] })
        queryClient.invalidateQueries({ queryKey: UNIFIED_TEAM_INVITATIONS_KEYS.all })
        queryClient.invalidateQueries({ queryKey: ['categories'] })
        queryClient.invalidateQueries({ queryKey: ['projections'] })
      }
    })

    return () => subscription.unsubscribe()
  }, [queryClient, cleanupQueryClient])

  /**
   * Sign in with email and password
   * After successful auth, checks organization suspension via edge function
   */
  const signIn = useCallback(async (email: string, password: string): Promise<SignInResult> => {
    if (!supabase) {
      return { data: { user: null, session: null }, error: { message: 'Supabase not configured', name: 'AuthError', status: 500 } as AuthError }
    }

    // Block auth state updates during verification
    isVerifyingRef.current = true

    try {
      // Step 1: Authenticate with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error || !data.user || !data.session) {
        isVerifyingRef.current = false
        return { data, error }
      }

      // Step 2: Check organization suspension status via edge function
      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-settings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${data.session.access_token}`,
          },
          body: JSON.stringify({ action: 'check-user-suspended' }),
        }
      )

      const result = await response.json()

      // Handle edge function errors
      if (!response.ok) {
        console.error('Organization check failed:', result)
        await supabase.auth.signOut()
        isVerifyingRef.current = false
        return {
          data: { user: null, session: null },
          error: {
            message: result.error || 'Failed to verify organization access',
            name: 'AuthError',
            status: response.status,
          } as AuthError,
        }
      }

      // Check if user's organization is suspended
      if (result.data?.isSuspended) {
        await supabase.auth.signOut()
        isVerifyingRef.current = false
        return {
          data: { user: null, session: null },
          error: {
            message: result.data.reason || 'Your organization account has been suspended.',
            name: 'AuthError',
            status: 403,
          } as AuthError,
        }
      }

      // Step 3: Check if user has MFA enabled and needs to verify.
      // Wrapped in try-catch so a transient AAL check failure doesn't block login entirely —
      // the user simply proceeds without the MFA step (same tradeoff as checkMFAPending).
      try {
        const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
        if (aal && aal.nextLevel === 'aal2' && aal.currentLevel !== 'aal2') {
          // MFA is required — set isMFAPending so LoginPage shows MFA step.
          // Keep isVerifyingRef = true so onAuthStateChange doesn't interfere.
          // user stays null so ProtectedRoute/AuthLayout don't redirect to dashboard.
          setState({ user: null, session: null, isLoading: false, isMFAPending: true })
          return { data, error: null, requiresMFA: true }
        }
      } catch (error) {
        console.error('AAL check failed during sign-in, skipping MFA step:', error)
      }

      // Login successful (no MFA required) - update state and unblock
      isVerifyingRef.current = false
      // The session is valid again, so the expiry notice must go — and, more
      // importantly, its sessionStorage flag with it, or the next reload would
      // announce an expiry that this very call just resolved (DCP-365 AC.4).
      // Done here rather than only in the SIGNED_IN listener below because that
      // listener returns early while isVerifyingRef is true, which is exactly
      // the window a normal sign-in runs in.
      clearSessionExpiredToast()
      setState({ user: data.user, session: data.session, isLoading: false, isMFAPending: false })

      // Invalidate all caches to ensure fresh data for the new session
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      queryClient.invalidateQueries({ queryKey: ['accessible-organizations'] })
      queryClient.invalidateQueries({ queryKey: ['banking'] })
      queryClient.invalidateQueries({ queryKey: ['plaid'] })
      queryClient.invalidateQueries({ queryKey: ['facilities'] })
      queryClient.invalidateQueries({ queryKey: ['team-members'] })
      queryClient.invalidateQueries({ queryKey: UNIFIED_TEAM_INVITATIONS_KEYS.all })
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      queryClient.invalidateQueries({ queryKey: ['projections'] })

      return { data, error: null }
    } catch (e) {
      console.error('Login failed:', e)
      await supabase.auth.signOut()
      isVerifyingRef.current = false
      return {
        data: { user: null, session: null },
        error: {
          message: 'Failed to verify organization access. Please try again.',
          name: 'AuthError',
          status: 500,
        } as AuthError,
      }
    }
  }, [queryClient])

  /**
   * Finalizes login after a successful MFA challengeAndVerify call.
   * Reads the current session (now upgraded to aal2), sets auth state,
   * and unblocks the auth listener so AuthLayout can redirect.
   */
  const completeMFALogin = useCallback(async (): Promise<{ error: AuthError | null }> => {
    if (!supabase) return { error: null }

    const { data: { session } } = await supabase.auth.getSession()

    if (!session) {
      isVerifyingRef.current = false
      setState({ user: null, session: null, isLoading: false, isMFAPending: false })
      return { error: { message: 'Session expired. Please sign in again.', name: 'AuthError', status: 401 } as AuthError }
    }

    // Re-check organization suspension — it may have changed between password auth and MFA
    try {
      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-settings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ action: 'check-user-suspended' }),
        }
      )
      const result = await response.json()

      if (!response.ok || result.data?.isSuspended) {
        await supabase.auth.signOut()
        isVerifyingRef.current = false
        setState({ user: null, session: null, isLoading: false, isMFAPending: false })
        return {
          error: {
            message: result.data?.reason || 'Your organization account has been suspended.',
            name: 'AuthError',
            status: 403,
          } as AuthError,
        }
      }
    } catch (error) {
      // If org check fails, allow access rather than blocking — same tradeoff as checkMFAPending
      console.error('Organization suspension check failed during MFA completion:', error)
    }

    isVerifyingRef.current = false
    // Same reason as in signIn: this path also completes behind the listener's
    // isVerifyingRef guard (DCP-365 AC.4).
    clearSessionExpiredToast()
    setState({
      user: session.user,
      session,
      isLoading: false,
      isMFAPending: false,
    })

    // Invalidate all caches for the new aal2 session
    queryClient.invalidateQueries({ queryKey: ['profile'] })
    queryClient.invalidateQueries({ queryKey: ['accessible-organizations'] })
    queryClient.invalidateQueries({ queryKey: ['banking'] })
    queryClient.invalidateQueries({ queryKey: ['plaid'] })
    queryClient.invalidateQueries({ queryKey: ['facilities'] })
    queryClient.invalidateQueries({ queryKey: ['team-members'] })
    queryClient.invalidateQueries({ queryKey: UNIFIED_TEAM_INVITATIONS_KEYS.all })
    queryClient.invalidateQueries({ queryKey: ['categories'] })
    queryClient.invalidateQueries({ queryKey: ['projections'] })

    return { error: null }
  }, [queryClient])

  /**
   * Sign out the current user.
   *
   * Sets isExplicitSignOutRef BEFORE supabase.auth.signOut() so the
   * onAuthStateChange SIGNED_OUT handler suppresses the session-expired toast
   * for this intentional flow.
   */
  const signOut = useCallback(async (): Promise<{ error: AuthError | null }> => {
    // Always reset the verifying flag so subsequent signIn attempts aren't blocked.
    // This covers the MFA "Back to login" flow where signIn set the ref to true.
    isVerifyingRef.current = false

    // Mark this sign-out as explicit so SIGNED_OUT handler skips the expiry toast.
    isExplicitSignOutRef.current = true

    // Dismiss any lingering session-expired toast from a prior expiry event.
    clearSessionExpiredToast()

    if (!supabase) {
      return { error: null }
    }

    // Clear all tenant caches (wrappers + Supabase client instances) and React Query cache.
    // Awaiting cleanupQueryClient ensures in-flight queries are cancelled before
    // supabase.auth.signOut() invalidates the session — without this, lingering
    // requests can retry with a stale token and surface 401 toasts after sign-out.
    // No explicit navigate here — ProtectedRoute reacts to user=null from the
    // SIGNED_OUT listener below and redirects to /login.
    clearTenantDbCache()
    clearTenantClients()
    await cleanupQueryClient()

    const { error } = await supabase.auth.signOut()
    return { error }
  }, [cleanupQueryClient])

  const value: AuthContextValue = {
    ...state,
    signIn,
    signOut,
    completeMFALogin,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }

  return context
}